import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Warning } from '@phosphor-icons/react';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomPage = () => {
  const { slug } = useParams();
  const { user } = useAuth();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        const response = await axios.get(`${API}/pages/${slug}`);
        setPage(response.data);
      } catch (err) {
        setError(err.response?.status === 404 ? 'Page not found' : 'Failed to load page');
      } finally {
        setLoading(false);
      }
    };
    fetchPage();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#c9a227] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <Warning size={64} className="mx-auto text-[#c9a227] mb-4" />
          <h1 className="text-3xl font-black text-white mb-2">{error}</h1>
          <p className="text-white/50 mb-6">The page you're looking for doesn't exist or has been removed.</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#c9a227] text-black font-bold hover:bg-[#b8922a] transition-colors"
          >
            <ArrowLeft size={20} />
            Back to Home
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Header */}
      <header className="bg-[#111111] border-b border-[#222]">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to={user ? "/community" : "/"} className="text-2xl font-black text-[#c9a227]">POWR</Link>
            <Link to="/community" className="text-white/60 hover:text-white text-sm transition-colors">
              Back to Community
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-6 py-12 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-4xl md:text-5xl font-black text-white mb-8">{page.title}</h1>
          
          {/* Render content - supports basic HTML */}
          <div 
            className="prose prose-invert prose-lg max-w-none
              prose-headings:text-white prose-headings:font-black
              prose-p:text-white/70 prose-p:leading-relaxed
              prose-a:text-[#c9a227] prose-a:no-underline hover:prose-a:underline
              prose-strong:text-white
              prose-ul:text-white/70 prose-ol:text-white/70
              prose-li:marker:text-[#c9a227]"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(page.content) }}
          />
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="border-t border-[#222] py-8 mt-12">
        <div className="container mx-auto px-6 text-center text-white/40 text-sm">
          <p>&copy; {new Date().getFullYear()} POWR | Brotherhood Platform</p>
        </div>
      </footer>
    </div>
  );
};

export default CustomPage;
